import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import type { BaseCategory } from "@usercentrics/cmp-browser-sdk";

import { IconChevronDown } from "@dfp/react-icons";

type CategoryAccordionsProps = {
  categories: BaseCategory[];
  expanded: string | null;
  onToggleExpanded: (slug: string) => void;
  isCategoryEnabled: (category: BaseCategory) => boolean;
  onToggleCategory: (category: BaseCategory, enabled: boolean) => void;
};

/** Accordion per category with a master toggle; consent is per category, not per service. */
export function CategoryAccordions({
  categories,
  expanded,
  onToggleExpanded,
  isCategoryEnabled: categoryEnabled,
  onToggleCategory,
}: CategoryAccordionsProps) {
  return (
    <Stack spacing={2}>
      {categories.map((category) => {
        const visibleServices = category.services.filter(
          (service) => !service.isHidden,
        );
        const isOpen = expanded === category.slug;
        return (
          <Accordion
            key={category.slug}
            disableGutters
            expanded={isOpen}
            onChange={() => onToggleExpanded(category.slug)}
            sx={{ bgcolor: "background.main", border: 0, boxShadow: "none" }}
          >
            <AccordionSummary>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ alignItems: "center", width: "100%" }}
              >
                <IconChevronDown
                  width={20}
                  height={20}
                  style={{
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 150ms",
                    flexShrink: 0,
                  }}
                />
                <Typography variant="h4" component="span">
                  {category.label}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Box
                  component="span"
                  onClick={(event) => event.stopPropagation()}
                  sx={{ display: "inline-flex" }}
                >
                  <Switch
                    checked={categoryEnabled(category)}
                    disabled={category.isEssential}
                    onChange={(event) =>
                      onToggleCategory(category, event.target.checked)
                    }
                    slotProps={{ input: { "aria-label": category.label } }}
                  />
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {category.description && (
                  <Typography variant="body2" color="text.secondary">
                    {category.description}
                  </Typography>
                )}
                <Stack spacing={1}>
                  {visibleServices.map((service) => (
                    <Typography key={service.id} variant="subtitle2">
                      {service.name}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
